import * as THREE from 'three';
import { hashUnit } from '../../shared/rng.js';
import type { Building, Road, World } from '../../shared/types.js';
import { bayMetresFor, facadeSeedFor } from './facade.js';

/**
 * What hangs off a shopfront: the name over the door, and an awning over the window.
 *
 * The facade shader draws a shop's display window and its fascia; this puts the shop's own
 * name on that fascia, from the OSM node mapped inside the building (`Building.sign`). It is
 * the most specific thing in the game — the café on the corner of the street you are racing
 * down is called what it is actually called — and it costs two triangles a shop.
 *
 * **Which wall.** A building has several and only one faces the street. Each wall a few
 * metres long is tried by looking six metres out from its middle for the nearest road, and
 * the closest one wins. A shop with no road within reach of any wall — a unit in a courtyard
 * — gets nothing, which is right: its sign is not on a street you can drive down.
 *
 * **One texture for every name.** The names are drawn white-on-black into one atlas, one
 * cell each, and the shader paints the board and the letters in the two colours each sign
 * carries per vertex. A mask rather than colour, so the atlas is one channel — 8 MB where
 * RGBA would be 32 for the same white-on-black. The atlas holds `MAX_SIGNS`, and
 * when a city has more shops than that the ones nearest the race route are the ones written.
 */

const ATLAS_W = 2048;
const ATLAS_H = 4096;
const CELL_W = 512;
const CELL_H = 64;
const COLS = ATLAS_W / CELL_W;
const MAX_SIGNS = COLS * (ATLAS_H / CELL_H);

/** The fascia band in the facade shader, as heights above the building's foot. */
const FASCIA_BOTTOM = 3.14;
const SIGN_HEIGHT = 0.62;
/** How far a sign stands off the wall, so it does not fight it for depth. */
const SIGN_OFFSET = 0.06;
/** Look this far out from a wall for the street it faces. */
const PROBE = 6;
const REACH = 9;

/** An awning over the display window: top edge, how far out, how far down. */
const AWNING_TOP = 3.05;
const AWNING_DEPTH = 1.15;
const AWNING_DROP = 0.55;
const AWNING_VALANCE = 0.22;
const AWNING_CHANCE = 0.45;

/** Board and lettering, as pairs. Mostly dark boards with light letters, as most shop signs are. */
const SIGN_COLOURS: [number, number][] = [
  [0x1e3a2c, 0xeee6cf],
  [0x14213d, 0xf2e8d0],
  [0x5a1f1f, 0xf3e9d2],
  [0x1b1b1d, 0xe8c873],
  [0x1b1b1d, 0xf5f5f0],
  [0xece6d8, 0x1e2b38],
  [0x2e4a5c, 0xffffff],
  [0x7a2e1f, 0xf6eedc],
];

const AWNING_COLOURS = [0x2f5d3a, 0x6e1f24, 0x1f3352, 0x2b2b2b, 0xb3542a, 0x40576b, 0x6b5a3e];

export interface Storefronts {
  objects: THREE.Object3D[];
  triangles: number;
  /** Signs light up after dark, about half of them. */
  setNight(amount: number): void;
  dispose(): void;
}

/**
 * A name board that is not over a shopfront — a supermarket's, a filling station's canopy —
 * painted in the same atlas and drawn in the same mesh as the shop signs, so the city's
 * other names cost no texture and no draw call of their own. See `cityObjects.ts`.
 */
export interface SignBoard {
  text: string;
  /** Centre of the board's face, world space. */
  x: number;
  y: number;
  z: number;
  /** Unit vector the text reads along, for someone facing the board. */
  rx: number;
  rz: number;
  width: number;
  height: number;
  board: number;
  letters: number;
  lit: boolean;
  /** Same text, same atlas cell: a canopy's two faces and a totem's are one name. */
  key?: string;
}

export function buildStorefronts(world: World, baseOf: (b: Building) => number, boards: SignBoard[] = []): Storefronts {
  const roads = new RoadGrid(world.roads);
  const route = world.route.path;

  interface Front {
    b: Building;
    ax: number;
    az: number;
    bx: number;
    bz: number;
    nx: number;
    nz: number;
    len: number;
    base: number;
    routeDistance: number;
  }
  const fronts: Front[] = [];
  for (const b of world.buildings) {
    if (b.base === null || b.height < 7) continue;
    const n = b.poly.length / 2;
    let best: Omit<Front, 'b' | 'base' | 'routeDistance'> | null = null;
    let bestDistance = REACH;
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      const ax = b.poly[i * 2];
      const az = b.poly[i * 2 + 1];
      const bx = b.poly[j * 2];
      const bz = b.poly[j * 2 + 1];
      const len = Math.hypot(bx - ax, bz - az);
      if (len < 3.5) continue;
      const nx = (bz - az) / len;
      const nz = -(bx - ax) / len;
      const d = roads.distance((ax + bx) / 2 + nx * PROBE, (az + bz) / 2 + nz * PROBE);
      if (d < bestDistance) {
        bestDistance = d;
        best = { ax, az, bx, bz, nx, nz, len };
      }
    }
    if (!best) continue;
    const mx = (best.ax + best.bx) / 2;
    const mz = (best.az + best.bz) / 2;
    fronts.push({ ...best, b, base: baseOf(b), routeDistance: distanceToPath(route, mx, mz) });
  }

  // --- signs ---------------------------------------------------------------
  // The boards first: there are a few dozen of them in a city, and a supermarket's name is
  // the one a driver reads from furthest away.
  const boardCells = new Map<string, number>();
  for (const board of boards) {
    const key = board.key ?? board.text;
    if (!boardCells.has(key) && boardCells.size < MAX_SIGNS) boardCells.set(key, boardCells.size);
  }
  const named = fronts.filter((f) => f.b.sign);
  named.sort((p, q) => p.routeDistance - q.routeDistance || p.b.id - q.b.id);
  const signed = named.slice(0, MAX_SIGNS - boardCells.size);

  const canvas = document.createElement('canvas');
  canvas.width = ATLAS_W;
  canvas.height = ATLAS_H;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, ATLAS_W, ATLAS_H);
  ctx.fillStyle = '#fff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  const positions: number[] = [];
  const uvs: number[] = [];
  const board: number[] = [];
  const letters: number[] = [];
  const glows: number[] = [];
  const c = new THREE.Color();

  /** Writes a name into its cell, and returns how much of the cell's width the text took. */
  const write = (slot: number, shown: string, serif: boolean): { size: number; textFraction: number } => {
    const col = slot % COLS;
    const row = Math.floor(slot / COLS);
    const cx = col * CELL_W + CELL_W / 2;
    const cy = row * CELL_H + CELL_H / 2;
    let size = 42;
    ctx.font = `${serif ? 'italic 600' : '700'} ${size}px ${serif ? 'Georgia, serif' : "'Google Sans Flex', 'Roboto Flex', sans-serif"}`;
    const measured = ctx.measureText(shown).width;
    // Fit the cell, with a margin; a long name gets smaller letters, not a longer board.
    if (measured > CELL_W - 40) size = Math.floor((size * (CELL_W - 40)) / measured);
    ctx.font = `${serif ? 'italic 600' : '700'} ${size}px ${serif ? 'Georgia, serif' : "'Google Sans Flex', 'Roboto Flex', sans-serif"}`;
    ctx.fillText(shown, cx, cy + 2, CELL_W - 30);
    return { size, textFraction: Math.min(ctx.measureText(shown).width, CELL_W - 30) / CELL_W };
  };

  /**
   * Two triangles facing the reader, the cell's UVs cropped to the board's own aspect around
   * the text. Wound up-then-along: `r` runs to the reader's right, so the face points at them.
   */
  const emit = (
    slot: number,
    textFraction: number,
    mx: number, y0: number, mz: number,
    rx: number, rz: number,
    width: number, height: number,
    bg: number, fg: number, lit: boolean,
  ) => {
    const col = slot % COLS;
    const row = Math.floor(slot / COLS);
    const uSpan = Math.min(1, Math.max(textFraction + 0.08, (width / height) * (CELL_H / CELL_W)));
    const u0 = (col * CELL_W + CELL_W * (0.5 - uSpan / 2)) / ATLAS_W;
    const u1 = (col * CELL_W + CELL_W * (0.5 + uSpan / 2)) / ATLAS_W;
    const v0 = 1 - ((row + 1) * CELL_H) / ATLAS_H;
    const v1 = 1 - (row * CELL_H) / ATLAS_H;
    const y1 = y0 + height;
    const h = width / 2;
    const corners = [
      [mx + rx * h, y0, mz + rz * h, u1, v0],
      [mx - rx * h, y0, mz - rz * h, u0, v0],
      [mx - rx * h, y1, mz - rz * h, u0, v1],
      [mx + rx * h, y1, mz + rz * h, u1, v1],
    ];
    for (const k of [0, 2, 1, 0, 3, 2]) {
      const [x, y, z, u, v] = corners[k];
      positions.push(x, y, z);
      uvs.push(u, v);
      c.setHex(bg, THREE.SRGBColorSpace);
      board.push(c.r, c.g, c.b);
      c.setHex(fg, THREE.SRGBColorSpace);
      letters.push(c.r, c.g, c.b);
      glows.push(lit ? 1 : 0);
    }
  };

  const written = new Map<number, number>();
  for (const b of boards) {
    const slot = boardCells.get(b.key ?? b.text);
    if (slot === undefined) continue;
    let fraction = written.get(slot);
    if (fraction === undefined) {
      fraction = write(slot, b.text, false).textFraction;
      written.set(slot, fraction);
    }
    emit(slot, fraction, b.x, b.y - b.height / 2, b.z, b.rx, b.rz, b.width, b.height, b.board, b.letters, b.lit);
  }

  signed.forEach((f, index) => {
    const slot = boardCells.size + index;
    const text = f.b.sign!;
    const look = hashUnit(world.seed, f.b.id ^ 0x5f3759df);
    const [bg, fg] = SIGN_COLOURS[Math.floor(look * SIGN_COLOURS.length) % SIGN_COLOURS.length];
    const serif = (look * 7.3) % 1 < 0.4;
    const caps = (look * 3.1) % 1 < 0.5;
    const shown = caps ? text.toUpperCase() : text;
    const { size, textFraction } = write(slot, shown, serif);
    // The board is as wide as its name needs, within the wall it is on.
    const width = Math.min(f.len * 0.82, Math.max(2.2, 0.62 * shown.length * (size / 42) + 1.0), 9);
    // The wall's tangent runs to the left of someone facing it, so the text runs against it.
    const tx = (f.bx - f.ax) / f.len;
    const tz = (f.bz - f.az) / f.len;
    // About half the signs in a city are lit at night; the rest are just painted.
    emit(
      slot,
      textFraction,
      (f.ax + f.bx) / 2 + f.nx * SIGN_OFFSET,
      f.base + FASCIA_BOTTOM,
      (f.az + f.bz) / 2 + f.nz * SIGN_OFFSET,
      -tx,
      -tz,
      width,
      SIGN_HEIGHT,
      bg,
      fg,
      (look * 5.7) % 1 < 0.55,
    );
  });

  const objects: THREE.Object3D[] = [];
  const disposables: { dispose(): void }[] = [];
  let triangles = 0;
  const night = { value: 0 };

  if (positions.length) {
    const rgba = ctx.getImageData(0, 0, ATLAS_W, ATLAS_H).data;
    const red = new Uint8Array(ATLAS_W * ATLAS_H);
    // Flipped as it is copied: canvas rows run down, texture rows run up.
    for (let y = 0; y < ATLAS_H; y++) {
      const src = y * ATLAS_W * 4;
      const dst = (ATLAS_H - 1 - y) * ATLAS_W;
      for (let x = 0; x < ATLAS_W; x++) red[dst + x] = rgba[src + x * 4];
    }
    const mask = new THREE.DataTexture(red, ATLAS_W, ATLAS_H, THREE.RedFormat, THREE.UnsignedByteType);
    mask.generateMipmaps = true;
    mask.minFilter = THREE.LinearMipmapLinearFilter;
    mask.magFilter = THREE.LinearFilter;
    mask.anisotropy = 8;
    mask.needsUpdate = true;
    disposables.push(mask);

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(board, 3));
    geometry.setAttribute('letterColor', new THREE.Float32BufferAttribute(letters, 3));
    geometry.setAttribute('glow', new THREE.Float32BufferAttribute(glows, 1));
    geometry.computeVertexNormals();
    geometry.computeBoundingSphere();

    const material = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.7, metalness: 0 });
    material.onBeforeCompile = (shader) => {
      shader.uniforms.signMask = { value: mask };
      shader.uniforms.signNight = night;
      shader.vertexShader = shader.vertexShader
        .replace(
          '#include <common>',
          '#include <common>\nattribute vec3 letterColor;\nattribute float glow;\nvarying vec3 vLetter;\nvarying float vGlow;\nvarying vec2 vSignUv;',
        )
        .replace(
          '#include <begin_vertex>',
          '#include <begin_vertex>\n\tvLetter = letterColor;\n\tvGlow = glow;\n\tvSignUv = uv;',
        );
      shader.fragmentShader = shader.fragmentShader
        .replace(
          '#include <common>',
          '#include <common>\nuniform sampler2D signMask;\nuniform float signNight;\nvarying vec3 vLetter;\nvarying float vGlow;\nvarying vec2 vSignUv;',
        )
        .replace(
          '#include <color_fragment>',
          '\tfloat signInk = texture2D( signMask, vSignUv ).r;\n\tdiffuseColor.rgb = mix( vColor, vLetter, signInk );',
        )
        .replace(
          '#include <emissivemap_fragment>',
          '\ttotalEmissiveRadiance += vLetter * signInk * vGlow * signNight * 2.2 + vColor * vGlow * signNight * 0.15;',
        );
    };
    material.customProgramCacheKey = () => 'shopSign';
    const mesh = new THREE.Mesh(geometry, material);
    mesh.name = 'shop-signs';
    mesh.receiveShadow = true;
    mesh.matrixAutoUpdate = false;
    objects.push(mesh);
    disposables.push(geometry, material);
    triangles += positions.length / 9;
  }

  // --- awnings ---------------------------------------------------------------
  const awningPos: number[] = [];
  const awningCol: number[] = [];
  const awningUv: number[] = [];
  for (const f of fronts) {
    const look = hashUnit(world.seed, f.b.id ^ 0x2545f491);
    if (look > AWNING_CHANCE) continue;
    const bays = Math.max(1, Math.round(f.len / bayMetresFor(f.b.style, facadeSeedFor(world.seed, f.b.id))));
    const bay = f.len / bays;
    const colour = AWNING_COLOURS[Math.floor(((look * 9.7) % 1) * AWNING_COLOURS.length)];
    const striped = (look * 13.1) % 1 < 0.35 ? 1 : 0;
    const tx = (f.bx - f.ax) / f.len;
    const tz = (f.bz - f.az) / f.len;
    const yTop = f.base + AWNING_TOP;
    const yLow = yTop - AWNING_DROP;
    // Over every window but the door's, which is the middle bay — the facade puts it there.
    const door = Math.floor(bays / 2);
    for (let k = 0; k < bays; k++) {
      if (k === door && bays > 1) continue;
      const s0 = k * bay + bay * 0.08;
      const s1 = (k + 1) * bay - bay * 0.08;
      const p = (s: number, out: number, y: number) => [
        f.ax + tx * s + f.nx * (out + 0.02),
        y,
        f.az + tz * s + f.nz * (out + 0.02),
      ];
      const a0 = p(s0, 0, yTop);
      const a1 = p(s1, 0, yTop);
      const b0 = p(s0, AWNING_DEPTH, yLow);
      const b1 = p(s1, AWNING_DEPTH, yLow);
      const c0 = p(s0, AWNING_DEPTH, yLow - AWNING_VALANCE);
      const c1 = p(s1, AWNING_DEPTH, yLow - AWNING_VALANCE);
      const quad = (q: number[][], u0: number, u1: number) => {
        for (const [i, u] of [[0, u0], [1, u1], [2, u1], [0, u0], [2, u1], [3, u0]] as [number, number][]) {
          awningPos.push(q[i][0], q[i][1], q[i][2]);
          awningUv.push(u * (s1 - s0), striped);
          c.setHex(colour, THREE.SRGBColorSpace);
          awningCol.push(c.r, c.g, c.b);
        }
      };
      // The canvas slope, then the valance hanging off its front edge.
      quad([a0, a1, b1, b0], 0, 1);
      quad([b0, b1, c1, c0], 0, 1);
    }
  }
  if (awningPos.length) {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(awningPos, 3));
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(awningCol, 3));
    geometry.setAttribute('uv', new THREE.Float32BufferAttribute(awningUv, 2));
    geometry.computeVertexNormals();
    geometry.computeBoundingSphere();
    const material = new THREE.MeshStandardMaterial({
      vertexColors: true,
      roughness: 0.9,
      metalness: 0,
      // Seen from under the edge, the inside of the canvas has to draw too.
      side: THREE.DoubleSide,
    });
    material.onBeforeCompile = (shader) => {
      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', '#include <common>\nvarying vec2 vAwning;')
        .replace('#include <begin_vertex>', '#include <begin_vertex>\n\tvAwning = uv;');
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <common>', '#include <common>\nvarying vec2 vAwning;')
        .replace(
          '#include <color_fragment>',
          // Stripes a third of a metre wide, on the awnings that have them; filtered so a
          // striped awning across the street is a soft average, not a moiré.
          '#include <color_fragment>\n' +
            '\tfloat stripeW = max( fwidth( vAwning.x ) * 0.5, 1e-3 );\n' +
            '\tfloat stripeT = vAwning.x / 0.66;\n' +
            '\tfloat stripe = ( floor( stripeT + stripeW ) * 0.5 + clamp( fract( stripeT + stripeW ), 0.0, 0.5 ) - floor( stripeT - stripeW ) * 0.5 - clamp( fract( stripeT - stripeW ), 0.0, 0.5 ) ) / ( 2.0 * stripeW );\n' +
            '\tdiffuseColor.rgb = mix( diffuseColor.rgb, vec3( 0.86, 0.84, 0.78 ), stripe * vAwning.y );',
        );
    };
    material.customProgramCacheKey = () => 'awning';
    const mesh = new THREE.Mesh(geometry, material);
    mesh.name = 'awnings';
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.matrixAutoUpdate = false;
    objects.push(mesh);
    disposables.push(geometry, material);
    triangles += awningPos.length / 9;
  }

  return {
    objects,
    triangles,
    setNight(amount) {
      night.value = amount;
    },
    dispose() {
      for (const d of disposables) d.dispose();
    },
  };
}

/** Distance from a point to the nearest road centreline, through a coarse grid of segments. */
class RoadGrid {
  private static readonly CELL = 24;
  private readonly cells = new Map<number, number[]>();
  private readonly segs: number[] = [];
  private readonly halfWidths: number[] = [];

  constructor(roads: Road[]) {
    const C = RoadGrid.CELL;
    for (const road of roads) {
      for (let i = 0; i + 3 < road.pts.length; i += 2) {
        const index = this.halfWidths.length;
        const ax = road.pts[i];
        const az = road.pts[i + 1];
        const bx = road.pts[i + 2];
        const bz = road.pts[i + 3];
        this.segs.push(ax, az, bx, bz);
        this.halfWidths.push(road.width / 2);
        for (let cx = Math.floor(Math.min(ax, bx) / C); cx <= Math.floor(Math.max(ax, bx) / C); cx++) {
          for (let cz = Math.floor(Math.min(az, bz) / C); cz <= Math.floor(Math.max(az, bz) / C); cz++) {
            const key = cx * 73856093 + cz;
            const bucket = this.cells.get(key);
            if (bucket) bucket.push(index);
            else this.cells.set(key, [index]);
          }
        }
      }
    }
  }

  /** Metres from the point to the nearest road's edge; Infinity if none is near. */
  distance(x: number, z: number): number {
    const C = RoadGrid.CELL;
    const cx = Math.floor(x / C);
    const cz = Math.floor(z / C);
    let best = Infinity;
    for (let dx = -1; dx <= 1; dx++) {
      for (let dz = -1; dz <= 1; dz++) {
        const bucket = this.cells.get((cx + dx) * 73856093 + cz + dz);
        if (!bucket) continue;
        for (const i of bucket) {
          const d = segmentDistance(x, z, this.segs[i * 4], this.segs[i * 4 + 1], this.segs[i * 4 + 2], this.segs[i * 4 + 3]);
          best = Math.min(best, d - this.halfWidths[i]);
        }
      }
    }
    return best;
  }
}

function segmentDistance(px: number, pz: number, ax: number, az: number, bx: number, bz: number): number {
  const dx = bx - ax;
  const dz = bz - az;
  const len2 = dx * dx + dz * dz;
  const t = len2 > 0 ? Math.max(0, Math.min(1, ((px - ax) * dx + (pz - az) * dz) / len2)) : 0;
  return Math.hypot(px - ax - dx * t, pz - az - dz * t);
}

function distanceToPath(path: number[], x: number, z: number): number {
  let best = Infinity;
  for (let i = 0; i + 3 < path.length; i += 2) {
    best = Math.min(best, segmentDistance(x, z, path[i], path[i + 1], path[i + 2], path[i + 3]));
  }
  return best;
}
